/**
 * useVulnerabilitySelection Hook
 *
 * Manages row selection state for the vulnerabilities table using key-based selection.
 * This pattern matches useSeedSelection for reliability across pagination and filtering.
 *
 * Key-based selection ensures that:
 * - Selected vulnerabilities persist across pagination
 * - Selection survives filtering and sorting
 * - Actions operate on correct vulnerabilities even after data changes
 *
 * Pattern: Key-based selection (stores risk.key instead of row indices)
 * Risk Level: Low
 * - Uses proper React dependencies for optimal performance
 * - Key-based selection survives pagination, sorting, and filtering
 * - Gracefully handles filtered or deleted vulnerabilities
 */

import { useCallback, useMemo, useState } from 'react';

import { Risk } from '@/types';

/**
 * Hook parameters
 */
export interface UseVulnerabilitySelectionParams {
  /** Array of vulnerability data */
  data: Risk[];
}

/**
 * Hook return value
 */
export interface UseVulnerabilitySelectionReturn {
  /** Array of selected vulnerability keys */
  selectedKeys: string[];
  /** Direct setter for selected keys array (for multi-select support) */
  setSelectedKeys: (keys: string[]) => void;
  /** Boolean indicating if any rows are selected */
  hasSelection: boolean;
  /** Array of actual risk objects that are selected */
  selectedVulnerabilities: Risk[];
  /** Function to toggle selection of a vulnerability by key */
  toggleSelection: (key: string) => void;
  /** Function to clear all selections */
  clearSelection: () => void;
  /** Function to select all vulnerabilities */
  selectAll: () => void;
  /** Count of selected vulnerabilities */
  selectedCount: number;
  /** Function to check if a key is selected */
  isSelected: (key: string) => boolean;

  // Legacy compatibility methods (deprecated - use selectedVulnerabilities and selectedKeys directly)
  /** @deprecated Use selectedVulnerabilities directly instead */
  getSelected: () => Risk[];
  /** @deprecated Use selectedKeys directly instead */
  getSelectedKeys: () => string[];
  /** @deprecated Use selectedKeys for Table component instead */
  selectedRows: string[];
  /** @deprecated Use setSelectedKeys for Table component instead */
  setSelectedRows: (keys: string[]) => void;
}

/**
 * Hook for managing vulnerability row selection state using key-based selection.
 *
 * Uses key-based selection for reliability across pagination, filtering, and sorting.
 * Provides helper methods for common selection operations.
 *
 * @param params - Hook configuration parameters
 * @returns Selection state and computed values
 *
 * @example
 * ```tsx
 * const { data } = useGetRisks();
 * const {
 *   selectedKeys,
 *   setSelectedKeys,
 *   selectedVulnerabilities,
 *   toggleSelection,
 *   clearSelection,
 *   isSelected
 * } = useVulnerabilitySelection({ data });
 *
 * // Use in Table selection
 * <Table
 *   selection={{
 *     value: selectedKeys,
 *     onChange: setSelectedKeys,
 *     selectAll: true
 *   }}
 * />
 *
 * // Use selected vulnerabilities for actions
 * <VulnerabilityActions vulnerabilities={selectedVulnerabilities} />
 *
 * // Toggle individual vulnerability
 * toggleSelection(risk.key);
 *
 * // Clear all selections
 * clearSelection();
 * ```
 */
export function useVulnerabilitySelection({
  data,
}: UseVulnerabilitySelectionParams): UseVulnerabilitySelectionReturn {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // Compute selected vulnerabilities from keys
  // Uses key-based lookup for reliability across pagination and filtering
  const { hasSelection, selectedVulnerabilities } = useMemo(() => {
    const computedSelectedVulnerabilities = selectedKeys
      .map(key => {
        const risk = data.find(r => r.key === key);

        if (!risk) {
          // Vulnerability no longer in current view (filtered out, deleted, etc.)
          // This is expected behavior and not an error
          return null;
        }

        return risk;
      })
      .filter((risk): risk is Risk => risk !== null);

    return {
      hasSelection: selectedKeys.length > 0,
      selectedVulnerabilities: computedSelectedVulnerabilities,
    };
  }, [selectedKeys, data]);

  // Toggle selection for a single vulnerability
  const toggleSelection = useCallback((key: string) => {
    setSelectedKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }, []);

  // Clear all selected rows
  const clearSelection = useCallback(() => {
    setSelectedKeys([]);
  }, []);

  // Select all vulnerabilities
  const selectAll = useCallback(() => {
    setSelectedKeys(data.map(risk => risk.key));
  }, [data]);

  // Check if a key is selected
  const isSelected = useCallback(
    (key: string) => selectedKeys.includes(key),
    [selectedKeys]
  );

  // Legacy compatibility methods (deprecated)
  const getSelected = useCallback(() => {
    return selectedVulnerabilities;
  }, [selectedVulnerabilities]);

  const getSelectedKeys = useCallback(() => {
    return selectedKeys;
  }, [selectedKeys]);

  return {
    selectedKeys,
    setSelectedKeys,
    hasSelection,
    selectedVulnerabilities,
    toggleSelection,
    clearSelection,
    selectAll,
    selectedCount: selectedKeys.length,
    isSelected,

    // Legacy compatibility (deprecated)
    getSelected,
    getSelectedKeys,
    selectedRows: selectedKeys,
    setSelectedRows: setSelectedKeys,
  };
}
